import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faFilePdf, faFileLines, faXmark } from '@fortawesome/free-solid-svg-icons';
import { SiteContext, ToastContext } from '../../Contexts';
import { FileService } from '../../Services/FileService';
import { ToastFailureMessage } from '../../Models/Models';
import { EntityColors } from '../../Themes/EntityColors';
import { Utils } from '../../Services/Utils';
import FormShell from '../Form/FormShell';
import SaveButton from '../Buttons/SaveButton';
import DiscardButton from '../Buttons/DiscardButton';

const ALLOWED_EXTENSIONS = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.odt,.ods,.odp';

const MAX_UPLOAD_MB = 25;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

const FileUploadComponent: React.FC = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Nahratie súborov');
    }, []);

    const { uploadFiles } = FileService();
    const { currentSite }: any = useContext(SiteContext);
    const { setToast }: any = useContext(ToastContext);
    const { action, icon } = EntityColors.file;

    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const isDirty = selectedFiles.length > 0 && !uploading;

    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const isOverLimit = totalSize > MAX_UPLOAD_BYTES;

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setSelectedFiles(prev => {
            const existingNames = new Set(prev.map(f => f.name));
            return [...prev, ...files.filter(f => !existingNames.has(f.name))];
        });
        e.target.value = '';
    };

    const removeFile = (idx: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedFiles.length === 0) return;
        if (isOverLimit) {
            setToast({ message: ToastFailureMessage.filesTooLarge });
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append('websiteId', currentSite.id.toString());
        selectedFiles.forEach(file => {
            fd.append('files', file, file.name);
        });
        await uploadFiles(fd);
        setUploading(false);
    };

    return (
        <FormShell title="Nahrať súbory" icon={icon} accentColor={action} backHref="/file/table" isDirty={isDirty}>
            <form onSubmit={submit}>
                <fieldset
                    disabled={uploading}
                    className="st-upload-fieldset"
                    style={{ '--upload-accent': action, '--upload-border': action + '40', '--upload-tint': action + '08' } as React.CSSProperties}
                >

                    {/* Drop zone / file input */}
                    <div className="st-upload-dropzone-wrap">
                        <label className="st-upload-field-label">
                            Vyberte súbory
                        </label>
                        <label className={`st-upload-dropzone${uploading ? ' st-upload-dropzone--disabled' : ''}`}>
                            <FontAwesomeIcon icon={faFile} className="st-upload-dropzone-icon" />
                            <span className="st-upload-dropzone-text">
                                Kliknite sem pre výber súborov
                            </span>
                            <span className="st-upload-dropzone-ext">
                                {ALLOWED_EXTENSIONS.split(',').join(', ')}
                            </span>
                            <input
                                type="file"
                                multiple
                                accept={ALLOWED_EXTENSIONS}
                                onChange={handleFiles}
                                disabled={uploading}
                                className="d-none"
                            />
                        </label>
                    </div>

                    {/* Selected files list */}
                    {selectedFiles.length > 0 && (
                        <div className="st-upload-list">
                            {selectedFiles.map((file, idx) => {
                                const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
                                const { fileIcon, iconClass } = getFileStyle(ext);
                                return (
                                    <div key={idx} className="st-file-upload-row">
                                        <div className={`st-file-icon-badge st-file-icon-badge--sm ${iconClass}`}>
                                            <FontAwesomeIcon icon={fileIcon} />
                                        </div>
                                        <div className="st-upload-row-info">
                                            <p className="st-file-upload-filename">
                                                {file.name}
                                            </p>
                                            <p className="st-file-upload-filesize">
                                                {formatSize(file.size)}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeFile(idx)}
                                            disabled={uploading}
                                            className="st-file-upload-remove"
                                        >
                                            <FontAwesomeIcon icon={faXmark} className="st-upload-close-icon" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {uploading && (
                        <p className="st-upload-uploading-msg">
                            Nahrávam súbory, prosím nezatvárajte stránku...
                        </p>
                    )}

                    {selectedFiles.length > 0 && (
                        <p className={`st-upload-size${isOverLimit ? ' st-upload-size--over' : ''}`}>
                            Celková veľkosť: {formatSize(totalSize)} / {MAX_UPLOAD_MB} MB
                            {isOverLimit && '. Prekročený limit, odstráňte niektoré súbory.'}
                        </p>
                    )}

                    <div className="st-upload-actions">
                        <DiscardButton to="/file/table" disabled={uploading} isDirty={isDirty} />
                        <SaveButton
                            text="Nahrať"
                            disabled={selectedFiles.length === 0 || uploading || isOverLimit}
                            isLoading={uploading}
                        />
                    </div>
                </fieldset>
            </form>
        </FormShell>
    );
};

function getFileStyle(ext: string): { fileIcon: any; iconClass: string } {
    switch (ext) {
        case 'pdf':  return { fileIcon: faFilePdf,   iconClass: 'st-file-icon-badge--pdf' };
        case 'doc':
        case 'docx': return { fileIcon: faFileLines, iconClass: 'st-file-icon-badge--doc' };
        case 'xls':
        case 'xlsx': return { fileIcon: faFileLines, iconClass: 'st-file-icon-badge--xls' };
        case 'ppt':
        case 'pptx': return { fileIcon: faFileLines, iconClass: 'st-file-icon-badge--ppt' };
        case 'txt':
        case 'csv':  return { fileIcon: faFileLines, iconClass: 'st-file-icon-badge--txt' };
        default:     return { fileIcon: faFile,      iconClass: 'st-file-icon-badge--default' };
    }
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default FileUploadComponent;
